/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2023 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedHTMLMessage } from 'react-intl';
import {
  withStyles, Divider, Typography, Link, IconButton
} from '@material-ui/core';
import { inject } from 'mobx-react';
import * as shapes from '../shapes';
import env from '../env';
import ExternalViewer from './ExternalViewer';
import PearsonLogo from '../icons/PearsonLogo';
import ToolsIcon from '../icons/ToolsIcon';
import OzBetaEnded from './OzBetaEnded';
import * as constants from '../constants';

/**
 * A Modal that handles Aria Errors
 *
 * @author Deepa Vaidya
 */


const styles = () => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    maxWidth: 600,
    minHeight: '85vh',
    margin: '0 auto',
    textAlign: 'center',
    padding: '0px 24px'
  },
  description: {
    padding: '16px 0'
  },
  avatar: {
    margin: '0 auto',
    width: 100,
    height: 100,
    marginBottom: 30
  },
  dividerRoot: {
    height: 2,
    backgroundColor: '#E1E9FB',
    margin: '16px 0px 24px 0px',
    borderRadius: 4
  },
  title: {
    fontSize: '36px',
    fontWeight: 600,
    lineHeight: '40px',
    fontFamily: 'TT Commons',
    '@media(max-width: 768px)': {
      fontSize: '28px',
      fontWeight: 700
    }
  },
  subtitle: {
    fontSize: '16px',
    letterSpacing: 0.02,
    lineHeight: '24px',
    fontFamily: 'Hind'
  },
  iconButton: {
    '&:hover': {
      backgroundColor: '#FFFFFF'
    }
  },
  logoWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignContent: 'center',
    width: '100%',
    height: 80,
    borderBottom: '1px solid #E1E9FB'
  },
  logo: {
    width: 48,
    height: 48
  }
});

const OzErrorCard = (
  {
    language, langKeyPrefix, classes, isMentor, errorCode
  }
) => {
  const [openExternalViewer, setOpenExternalViewer] = useState(false);
  const iframeUrl = isMentor ? env.get('MENTOR_ONE_CRM_FORM_URL') : env.get('LEARNER_ONE_CRM_FORM_URL');
  const toggleExternalViewer = (event) => {
    event.preventDefault();
    setOpenExternalViewer(!openExternalViewer);
  };
  const gotoDashboard = () => {
    window.open(window.location.origin, '_self');
  };
  return (
    errorCode === constants.BETA_EXPIRED) ? (
      <OzBetaEnded />
    ) : (
      <div>
        <div className={classes.logoWrapper}>
          <IconButton
            id="logo"
            color="inherit"
            onClick={gotoDashboard}
            className={classes.iconButton}
          >
            <PearsonLogo onClick={gotoDashboard} />
          </IconButton>
        </div>
        <div className={classes.card}>
          <ToolsIcon className={classes.avatar} />
          <div>
            <Typography className={classes.title}>
              <FormattedHTMLMessage
                {...language.getText(`${langKeyPrefix}.HTTP_500_TITLE`)}
              />
            </Typography>
          </div>
          <Divider classes={{ root: classes.dividerRoot }} />
          <div>
            <Typography className={classes.subtitle}>
              <FormattedHTMLMessage
                {...language.getText(`${langKeyPrefix}.HTTP_500_DESC`)}
              />
            </Typography>
          </div>
        </div>
        <div className={classes.description}>
          <Typography className={classes.subtitle}>
            <FormattedHTMLMessage
              {...language.getText(`${langKeyPrefix}.HTTP_500_SUB_DESC`)}
            />
            <Link
              id="support"
              href={iframeUrl}
              underline="always"
              onClick={toggleExternalViewer}
            >
              <FormattedHTMLMessage
                {...language.getText(`${langKeyPrefix}.HTTP_500_CUSTOMER_SUPPORT`)}
              />
            </Link>
          </Typography>
        </div>
        <ExternalViewer
          open={openExternalViewer}
          openExternalViewer={toggleExternalViewer}
          iframeUrl={iframeUrl}
        />
      </div>
    );
};

OzErrorCard.propTypes = {
  language: shapes.language.isRequired,
  classes: PropTypes.object.isRequired,
  textKey: PropTypes.string,
  langKeyPrefix: PropTypes.string,
  isMentor: PropTypes.bool.isRequired,
  errorCode: PropTypes.string.isRequired
};

OzErrorCard.defaultProps = {
  textKey: null,
  langKeyPrefix: 'errors'
};

export default inject('language')(withStyles(styles)(OzErrorCard));
